import axios from "axios";
import {
  type ChangeEvent,
  type FocusEvent,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  acceptPublisherAgreement,
  fetchCategories,
  fetchPublisherAgreementVersion,
  fetchPublisherAppForEdit,
  submitApplication,
  updatePublisherApp,
  type Category,
} from "../services/apps";
import { resolveCategoryOptions } from "../services/categoryOptions";
import {
  rememberMembershipPurchaseCheckoutAmount,
  reportPublisherMembershipPurchaseConversion,
} from "../services/googleAdsMembershipPurchase";
import { getPaymentStatus, initiateAppSubscription, verifyRazorpayAppPayment, cancelPublisherPayment } from "../services/payments";
import { openRazorpayCheckout } from "../services/razorpayCheckout";
import {
  clearPublishFormDraft,
  loadPublishFormDraft,
  mergeDraftWithReturnState,
  PUBLISHER_AGREEMENT_ROUTE,
  PUBLISH_YOUR_APP_ROUTE,
  savePublishFormDraft,
  shouldCheckAgreementOnReturn,
} from "../services/publisherAgreementFlow";
import {
  collectCreatePublishValidationErrors,
  collectEditPublishValidationErrors,
  resolvePostPublicationMessage,
  resolvePostPublicationRoute,
  validateApkFileOnSelect,
  validateAppIconFileChoice,
  validateScreenshotFileChoice,
} from "../services/publishAppValidation";
import {
  PUBLISH_APK_ACCEPT,
  PUBLISH_IMAGE_ACCEPT,
  applyValidatedFileSelection,
  uploadPreviewImageStyle,
} from "../services/publishAppFileSelection";
import { useObjectUrl } from "../hooks/useObjectUrl";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { evaluatePublishGuestAuthGate } from "../services/publishGuestAuthGate";
import { buildPublisherDashboardNavigationState } from "../services/publisherDashboard";

type FileFieldErrors = {
  apk?: string;
  icon?: string;
  screenshot1?: string;
  screenshot2?: string;
  screenshot3?: string;
};

type PublishFormValues = {
  appName: string;
  categoryId: string;
  problemSolved: string;
  shortDescription: string;
  version: string;
  developerWebsite: string;
  appForSale: boolean;
  publisherAgreement: boolean;
};

const initialValues: PublishFormValues = {
  appName: "",
  categoryId: "",
  problemSolved: "",
  shortDescription: "",
  version: "",
  developerWebsite: "",
  appForSale: true,
  publisherAgreement: false,
};

const PAYMENT_CANCELLED_RETRY_MESSAGE =
  "Payment was cancelled. You can retry membership payment below.";

export function PublishYourAppPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { applicationId: editApplicationId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(editApplicationId);
  const [values, setValues] = useState<PublishFormValues>(initialValues);
  const [categories, setCategories] = useState<Category[]>([]);
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [appIconFile, setAppIconFile] = useState<File | null>(null);
  const [screenshot1File, setScreenshot1File] = useState<File | null>(null);
  const [screenshot2File, setScreenshot2File] = useState<File | null>(null);
  const [screenshot3File, setScreenshot3File] = useState<File | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [membershipPaymentPending, setMembershipPaymentPending] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [paymentOpening, setPaymentOpening] = useState(false);
  const [paymentRetryError, setPaymentRetryError] = useState("");
  const [hasExistingIcon, setHasExistingIcon] = useState(false);
  const [existingScreenshotCount, setExistingScreenshotCount] = useState(0);
  const [fileFieldErrors, setFileFieldErrors] = useState<FileFieldErrors>({});

  const appIconPreviewUrl = useObjectUrl(appIconFile);
  const screenshot1PreviewUrl = useObjectUrl(screenshot1File);
  const screenshot2PreviewUrl = useObjectUrl(screenshot2File);
  const screenshot3PreviewUrl = useObjectUrl(screenshot3File);

  const pollPaymentUntilTerminal = useCallback(async (
    paymentId: string,
    paidApplicationId: string,
    amount?: { amountPaise: number; currency: string },
  ) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const payment = await getPaymentStatus(paymentId);
      if (payment.status === "succeeded") {
        setMembershipPaymentPending(false);
        reportPublisherMembershipPurchaseConversion({
          paymentId: payment.id,
          amountPaise: amount?.amountPaise,
          currency: amount?.currency,
        });
        navigate("/publisher-dashboard", {
          replace: true,
          state: buildPublisherDashboardNavigationState(
            paidApplicationId,
            "Payment completed. Your app submission is being processed.",
          ),
        });
        return;
      }

      if (payment.status === "failed" || payment.status === "cancelled") {
        setFormError("Payment did not complete. You can retry membership payment below.");
        return;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 1500));
    }

    setFormError("Payment verification is still in progress. Check your publisher dashboard shortly.");
  }, [navigate]);

  const openPublishRazorpayCheckout = useCallback(async (input: {
    applicationId: string;
    paymentId: string;
    orderId: string;
    razorpayKeyId: string;
    amountPaise: number;
    currency: string;
  }) => {
    rememberMembershipPurchaseCheckoutAmount(
      input.paymentId,
      input.amountPaise,
      input.currency,
    );

    try {
      await openRazorpayCheckout({
        keyId: input.razorpayKeyId,
        orderId: input.orderId,
        amountPaise: input.amountPaise,
        currency: input.currency,
        description: "Publish your app on OpenAppCart",
        onDismiss: () => {
          void cancelPublisherPayment(input.paymentId).catch(() => undefined);
          setPaymentRetryError("");
          setFormError(PAYMENT_CANCELLED_RETRY_MESSAGE);
        },
        onSuccess: async (response) => {
          setFormSuccess("Payment received. Verification in progress.");
          try {
            await verifyRazorpayAppPayment({
              paymentId: input.paymentId,
              orderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
          } catch {
            // Webhook or retry may still complete verification.
          }

          await pollPaymentUntilTerminal(input.paymentId, input.applicationId, {
            amountPaise: input.amountPaise,
            currency: input.currency,
          });
        },
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Razorpay checkout could not be opened.";
      setFormError(message);
    }
  }, [pollPaymentUntilTerminal]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const draft = loadPublishFormDraft();
    const accepted = shouldCheckAgreementOnReturn(location.state);
    if (draft || accepted) {
      setValues(mergeDraftWithReturnState(draft, initialValues, accepted));
    }

    if (accepted) {
      navigate(PUBLISH_YOUR_APP_ROUTE, { replace: true, state: null });
    }
  }, [isEditMode, location.state, navigate]);

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!editApplicationId) {
      return;
    }

    setLoadingEdit(true);
    setFormError("");

    void fetchPublisherAppForEdit(editApplicationId)
      .then((app) => {
        setValues({
          appName: app.name,
          categoryId: app.category_id ?? "",
          problemSolved: app.problem_solved ?? "",
          shortDescription: app.short_description ?? "",
          version: app.current_version ?? "",
          developerWebsite: app.developer_website ?? "",
          appForSale: app.apps_for_sale,
          publisherAgreement: true,
        });
        setHasExistingIcon(Boolean(app.icon_url));
        setExistingScreenshotCount(app.screenshots.length);
      })
      .catch(() => {
        setFormError("Unable to load this application for editing.");
      })
      .finally(() => {
        setLoadingEdit(false);
      });
  }, [editApplicationId]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const paymentState = searchParams.get("payment");
    const paymentId = searchParams.get("paymentId");
    const appId = searchParams.get("applicationId");

    if (!paymentState || !paymentId) {
      return;
    }

    if (appId) {
      setApplicationId(appId);
      setMembershipPaymentPending(true);
    }

    if (paymentState === "success") {
      void getPaymentStatus(paymentId)
        .then((payment) => {
          if (payment.status === "succeeded" && appId) {
            reportPublisherMembershipPurchaseConversion({
              paymentId: payment.id,
            });
            navigate("/publisher-dashboard", {
              replace: true,
              state: buildPublisherDashboardNavigationState(
                appId,
                "Payment completed. Your app submission is being processed.",
              ),
            });
          }
        })
        .catch(() => undefined);
    }

    if (paymentState === "pending" && searchParams.get("provider") === "razorpay" && appId) {
      void initiateAppSubscription(appId, "razorpay")
        .then((result) => {
          if (
            result.providerOrderId &&
            result.razorpayKeyId &&
            typeof result.amountPaise === "number" &&
            result.currency
          ) {
            return openPublishRazorpayCheckout({
              applicationId: appId,
              paymentId: result.paymentId,
              orderId: result.providerOrderId,
              razorpayKeyId: result.razorpayKeyId,
              amountPaise: result.amountPaise,
              currency: result.currency,
            });
          }

          setFormError("Razorpay checkout is unavailable. Configure provider credentials.");
        })
        .catch(() => {
          setFormError("Unable to resume Razorpay checkout.");
        });
    }

    if (paymentState === "cancelled") {
      setMembershipPaymentPending(true);
      setPaymentRetryError("");
      setFormError(PAYMENT_CANCELLED_RETRY_MESSAGE);
    }
  }, [navigate, searchParams, isEditMode, openPublishRazorpayCheckout]);

  const categoryOptions = useMemo(
    () => resolveCategoryOptions(categories),
    [categories],
  );

  const publishGuestGate = useMemo(
    () =>
      isEditMode
        ? { action: "allow" as const }
        : evaluatePublishGuestAuthGate({
            isLoading: authLoading,
            user,
            fromPath: location.pathname,
          }),
    [authLoading, isEditMode, location.pathname, user],
  );

  const paymentRetryAvailable = useMemo(
    () => !isEditMode && membershipPaymentPending && Boolean(applicationId),
    [applicationId, isEditMode, membershipPaymentPending],
  );

  const requirePublisherAuthForAction = useCallback((): boolean => {
    if (isEditMode) {
      return false;
    }

    const gate = publishGuestGate;

    if (gate.action === "wait") {
      return true;
    }

    if (gate.action === "redirect") {
      navigate(gate.to, { state: gate.state });
      return true;
    }

    return false;
  }, [isEditMode, navigate, publishGuestGate]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    if (requirePublisherAuthForAction()) {
      event.preventDefault();
      return;
    }

    const target = event.target;
    const { name } = target;
    const nextValue =
      target instanceof HTMLInputElement && target.type === "checkbox"
        ? target.checked
        : target.value;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: nextValue,
    }));
  };

  const handleAgreementLinkClick = () => {
    savePublishFormDraft(values);
  };

  const handleApkChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (requirePublisherAuthForAction()) {
      event.target.value = "";
      return;
    }

    const input = event.currentTarget;
    const selected = input.files?.[0] ?? null;

    if (!selected) {
      setApkFile(null);
      setFileFieldErrors((current) => ({ ...current, apk: undefined }));
      return;
    }

    const error = validateApkFileOnSelect(selected);
    if (error) {
      setApkFile(null);
      setFileFieldErrors((current) => ({ ...current, apk: error }));
      input.value = "";
      return;
    }

    setApkFile(selected);
    setFileFieldErrors((current) => ({ ...current, apk: undefined }));
  };

  const handleAppIconChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (requirePublisherAuthForAction()) {
      event.target.value = "";
      return;
    }

    const { file, error } = applyValidatedFileSelection(
      event.target.files?.[0] ?? null,
      validateAppIconFileChoice,
    );

    setAppIconFile(file);
    setFileFieldErrors((current) => ({ ...current, icon: error }));

    if (error) {
      event.target.value = "";
    }
  };

  const handleScreenshotChange =
    (index: 1 | 2 | 3) => (event: ChangeEvent<HTMLInputElement>) => {
      if (requirePublisherAuthForAction()) {
        event.target.value = "";
        return;
      }

      const fieldKey = `screenshot${index}` as keyof FileFieldErrors;
      const setScreenshotFile =
        index === 1 ? setScreenshot1File : index === 2 ? setScreenshot2File : setScreenshot3File;
      const { file, error } = applyValidatedFileSelection(
        event.target.files?.[0] ?? null,
        validateScreenshotFileChoice,
      );

      setScreenshotFile(file);
      setFileFieldErrors((current) => ({ ...current, [fieldKey]: error }));

      if (error) {
        event.target.value = "";
      }
    };

  const handleFileFieldActivate = (
    event: MouseEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>,
  ) => {
    if (requirePublisherAuthForAction()) {
      event.preventDefault();
    }
  };

  function buildValidationInput() {
    return {
      appName: values.appName,
      categoryId: values.categoryId,
      problemSolved: values.problemSolved,
      shortDescription: values.shortDescription,
      version: values.version,
      developerWebsite: values.developerWebsite,
      publisherAgreement: values.publisherAgreement,
      apkFile,
      appIconFile,
      screenshot1File,
      screenshot2File,
      screenshot3File,
      hasExistingIcon,
      existingScreenshotCount,
    };
  }

  function collectCreateValidationErrors(): string[] {
    return collectCreatePublishValidationErrors(buildValidationInput());
  }

  function collectEditValidationErrors(): string[] {
    return collectEditPublishValidationErrors(buildValidationInput());
  }

  function collectValidationErrors(): string[] {
    return isEditMode
      ? collectEditValidationErrors()
      : collectCreateValidationErrors();
  }

  const isFormValid =
    collectValidationErrors().length === 0 &&
    Object.values(fileFieldErrors).every((error) => !error);

  async function handleEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!editApplicationId) {
      return;
    }

    const errors = collectEditValidationErrors();
    setValidationErrors(errors);
    if (errors.length > 0) {
      return;
    }

    const formData = new FormData();
    formData.append("name", values.appName.trim());
    formData.append("categoryId", values.categoryId);
    formData.append("problemSolved", values.problemSolved.trim());
    formData.append("shortDescription", values.shortDescription.trim());
    if (values.developerWebsite.trim()) {
      formData.append("developerWebsite", values.developerWebsite.trim());
    }
    formData.append("appsForSale", String(values.appForSale));

    if (appIconFile) {
      formData.append("icon", appIconFile);
    }

    const screenshotFiles = [screenshot1File, screenshot2File, screenshot3File].filter(
      Boolean,
    ) as File[];

    for (const screenshot of screenshotFiles) {
      formData.append("screenshots", screenshot);
    }

    setSubmitting(true);

    try {
      await updatePublisherApp(editApplicationId, formData);
      setFormSuccess("Application saved successfully.");
      navigate("/publisher-dashboard", { replace: true });
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : "Application update failed. Please review your details and try again.";
      const apiErrors = axios.isAxiosError(error)
        ? (error.response?.data?.errors as string[] | undefined)
        : undefined;
      setFormError(message);
      setValidationErrors(apiErrors ?? []);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (requirePublisherAuthForAction()) {
      return;
    }

    if (submitting || paymentOpening) {
      return;
    }

    if (membershipPaymentPending && applicationId) {
      await startRazorpayPayment(applicationId);
      return;
    }

    setFormError("");

    const errors = collectCreateValidationErrors();
    setValidationErrors(errors);
    if (errors.length > 0 || !apkFile) {
      return;
    }

    const formData = new FormData();
    formData.append("name", values.appName.trim());
    formData.append("categoryId", values.categoryId);
    formData.append("problemSolved", values.problemSolved.trim());
    formData.append("shortDescription", values.shortDescription.trim());
    formData.append("version", values.version.trim());
    if (values.developerWebsite.trim()) {
      formData.append("developerWebsite", values.developerWebsite.trim());
    }
    formData.append("appsForSale", String(values.appForSale));
    formData.append("publisherAgreement", "true");
    formData.append("apk", apkFile);
    formData.append("icon", appIconFile!);
    formData.append("screenshots", screenshot1File!);
    formData.append("screenshots", screenshot2File!);
    formData.append("screenshots", screenshot3File!);

    setSubmitting(true);

    try {
      const agreementVersion = await fetchPublisherAgreementVersion();
      await acceptPublisherAgreement(agreementVersion);

      const result = await submitApplication(formData);
      setApplicationId(result.application.id);
      clearPublishFormDraft();

      if (result.application.hasActiveSubscription) {
        const message = resolvePostPublicationMessage(result.application.publication);
        const route = resolvePostPublicationRoute(result.application.publication);
        navigate(route, {
          replace: true,
          state:
            route === "/publisher-dashboard"
              ? buildPublisherDashboardNavigationState(result.application.id, message)
              : message
                ? { publishMessage: message }
                : null,
        });
        return;
      }

      setMembershipPaymentPending(true);
      setSubmitting(false);
      await startRazorpayPayment(result.application.id);
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : "Application submission failed. Please review your details and try again.";
      const apiErrors = axios.isAxiosError(error)
        ? (error.response?.data?.errors as string[] | undefined)
        : undefined;
      setFormError(message);
      setValidationErrors(apiErrors ?? []);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    if (isEditMode) {
      await handleEditSubmit(event);
      return;
    }

    await handleCreateSubmit(event);
  }

  if (isEditMode && loadingEdit) {
    return (
      <main className="oac-publish-scale-frame oac-publish-scale-frame--edit">
        <div className="oac-publish oac-publish--edit">
          <p className="oac-form-message">Loading application...</p>
        </div>
      </main>
    );
  }

  async function startRazorpayPayment(appId?: string) {
    if (requirePublisherAuthForAction()) {
      return;
    }

    const targetApplicationId = appId ?? applicationId;
    if (!targetApplicationId || paymentOpening) {
      return;
    }

    setPaymentOpening(true);
    setPaymentRetryError("");

    try {
      const result = await initiateAppSubscription(targetApplicationId, "razorpay");

      if (result.status === "active") {
        setMembershipPaymentPending(false);
        const message = resolvePostPublicationMessage(result.publication);
        const route = resolvePostPublicationRoute(result.publication);
        navigate(route, {
          replace: true,
          state:
            route === "/publisher-dashboard"
              ? buildPublisherDashboardNavigationState(targetApplicationId, message)
              : message
                ? { publishMessage: message }
                : null,
        });
        return;
      }

      if (
        result.providerOrderId &&
        result.razorpayKeyId &&
        typeof result.amountPaise === "number" &&
        result.currency
      ) {
        await openPublishRazorpayCheckout({
          applicationId: targetApplicationId,
          paymentId: result.paymentId,
          orderId: result.providerOrderId,
          razorpayKeyId: result.razorpayKeyId,
          amountPaise: result.amountPaise,
          currency: result.currency,
        });
        return;
      }

      setPaymentRetryError("Razorpay checkout is unavailable. Configure provider credentials.");
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : "Payment could not be started.";
      setPaymentRetryError(message);
    } finally {
      setPaymentOpening(false);
    }
  }

  const setNotForSale = () => {
    setValues((currentValues) => ({ ...currentValues, appForSale: false }));
  };

  const setForSale = () => {
    setValues((currentValues) => ({ ...currentValues, appForSale: true }));
  };

  const isProtectedInteractiveTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest(
        [
          "input",
          "select",
          "textarea",
          "button",
          ".oac-publish__button-wrap",
          ".oac-switch[role='button']",
          ".oac-publish__apk-upload",
          ".oac-upload--icon",
          ".oac-upload--screenshot",
        ].join(", "),
      ),
    );
  }, []);

  const handleProtectedInteractionCapture = useCallback(
    (
      event:
        | PointerEvent<HTMLElement>
        | MouseEvent<HTMLElement>
        | FocusEvent<HTMLElement>,
    ) => {
      if (!isProtectedInteractiveTarget(event.target)) {
        return;
      }

      if (requirePublisherAuthForAction()) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isProtectedInteractiveTarget, requirePublisherAuthForAction],
  );

  return (
    <main
      className={`oac-publish-scale-frame${isEditMode ? " oac-publish-scale-frame--edit" : ""}`}
    >
      <div className={`oac-publish${isEditMode ? " oac-publish--edit" : ""}`}>
        <header className="oac-publish__header">
          <h1>
            {isEditMode ? (
              "Edit Your App"
            ) : (
              <>
                <span className="oac-publish-heading__desktop">Publish Your App</span>
                <span className="oac-publish-heading__mobile" aria-hidden="true">
                  <span className="oac-publish-heading__mobile-line">Publish</span>
                  <span className="oac-publish-heading__mobile-line">Your App</span>
                </span>
              </>
            )}
          </h1>
          <p>
            {isEditMode ? (
              "Update your application details below."
            ) : (
              <>
                <span className="oac-publish-intro__desktop">
                  Complete the details below. Your app goes live instantly after
                  submission.
                </span>
                <span className="oac-publish-intro__mobile">
                  <span className="oac-publish-intro__mobile-line">
                    Complete the details below.
                  </span>
                  <span className="oac-publish-intro__mobile-line">
                    Your app goes live instantly after submission.
                  </span>
                </span>
              </>
            )}
          </p>
        </header>

        <form
          className={`oac-publish__form${isEditMode ? " oac-publish__form--edit" : ""}`}
          onFocusCapture={handleProtectedInteractionCapture}
          onMouseDownCapture={handleProtectedInteractionCapture}
          onPointerDownCapture={handleProtectedInteractionCapture}
          onSubmit={handleSubmit}
        >
          <section className="oac-publish__left">
            <div className="oac-publish__field">
              <label htmlFor="app-name">App name</label>
              <input
                id="app-name"
                name="appName"
                onChange={handleInputChange}
                placeholder="CryptoCart"
                required
                type="text"
                value={values.appName}
              />
            </div>

            <div className="oac-publish__field">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="categoryId"
                onChange={handleInputChange}
                required
                value={values.categoryId}
              >
                <option disabled value="">
                  Choose your app category
                </option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="oac-publish__field">
              <label htmlFor="problem">
                Problem your app solves (max 40 characters)
              </label>
              <input
                id="problem"
                maxLength={40}
                name="problemSolved"
                onChange={handleInputChange}
                placeholder="Find crypto-friendly merchants worldwide"
                required
                type="text"
                value={values.problemSolved}
              />
            </div>

            <div className="oac-publish__field">
              <label htmlFor="description">
                Short description (Max 300 characters)
              </label>
              <textarea
                id="description"
                maxLength={300}
                name="shortDescription"
                onChange={handleInputChange}
                placeholder="Describe your app"
                required
                value={values.shortDescription}
              />
            </div>

            {!isEditMode && (
              <div className="oac-publish__field">
                <label htmlFor="apk-file">APK file</label>
                <label className="oac-publish__apk-upload">
                  <input
                    accept={PUBLISH_APK_ACCEPT}
                    aria-label="Upload APK file"
                    className="oac-publish__apk-upload-input"
                    id="apk-file"
                    onChange={handleApkChange}
                    onClick={handleFileFieldActivate}
                    onKeyDown={handleFileFieldActivate}
                    required
                    type="file"
                  />
                  <span className="oac-publish__apk-upload-content">
                    {apkFile ? (
                      <span className="oac-publish__apk-filename">{apkFile.name}</span>
                    ) : (
                      <>
                        <span
                          aria-hidden="true"
                          className="oac-upload__symbol oac-publish__apk-upload-icon"
                        >
                          <span className="oac-upload__arrow">↑</span>
                          <span className="oac-upload__tray" />
                        </span>
                        <span aria-hidden="true" className="oac-publish__apk-upload-hint">
                          Upload .APK file
                        </span>
                      </>
                    )}
                  </span>
                </label>
                {fileFieldErrors.apk && (
                  <p className="oac-form-message oac-form-message--error">{fileFieldErrors.apk}</p>
                )}
              </div>
            )}

            <div className="oac-publish__field">
              <label htmlFor="version">Version</label>
              <input
                disabled={isEditMode}
                id="version"
                name="version"
                onChange={handleInputChange}
                placeholder="e.g. 1.0.0"
                readOnly={isEditMode}
                required={!isEditMode}
                type="text"
                value={values.version}
              />
            </div>
          </section>

          <section className="oac-publish__right">
            <div className="oac-publish__field">
              <label htmlFor="developer-website">
                Developer Website (optional)
              </label>
              <input
                id="developer-website"
                name="developerWebsite"
                onChange={handleInputChange}
                placeholder="CryptoCart"
                type="text"
                value={values.developerWebsite}
              />
            </div>

            <div className="oac-publish__sale-row">
              <span className="oac-publish__sale-title">Apps for sale</span>
              <span>No</span>
              <span
                aria-hidden="true"
                className={[
                  "oac-switch",
                  !values.appForSale ? "oac-switch--on" : "oac-switch--off",
                ].join(" ")}
                onClick={setNotForSale}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setNotForSale();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span />
              </span>
              <span>Yes</span>
              <span
                aria-hidden="true"
                className={[
                  "oac-switch",
                  values.appForSale ? "oac-switch--on" : "oac-switch--off",
                ].join(" ")}
                onClick={setForSale}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setForSale();
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span />
              </span>
            </div>

            <div className="oac-publish__upload-section">
              <div className="oac-publish__upload-labels">
                <span>App icon</span>
                <span>Screenshots</span>
              </div>

              <div className="oac-publish__uploads">
                <label
                  aria-label={
                    appIconFile ? `App icon selected: ${appIconFile.name}` : "App icon upload"
                  }
                  className="oac-upload oac-upload--icon"
                >
                  <input
                    accept={PUBLISH_IMAGE_ACCEPT}
                    onChange={handleAppIconChange}
                    onClick={handleFileFieldActivate}
                    onKeyDown={handleFileFieldActivate}
                    required={!isEditMode || !hasExistingIcon}
                    type="file"
                  />
                  {appIconPreviewUrl ? (
                    <img
                      alt=""
                      aria-hidden="true"
                      src={appIconPreviewUrl}
                      style={uploadPreviewImageStyle}
                    />
                  ) : (
                    <span aria-hidden="true" className="oac-upload__symbol">
                      <span className="oac-upload__arrow">↑</span>
                      <span className="oac-upload__tray" />
                    </span>
                  )}
                </label>

                {(
                  [
                    [1, screenshot1PreviewUrl, screenshot1File],
                    [2, screenshot2PreviewUrl, screenshot2File],
                    [3, screenshot3PreviewUrl, screenshot3File],
                  ] as const
                ).map(([number, previewUrl, selectedFile]) => (
                  <label
                    aria-label={
                      selectedFile
                        ? `Screenshot ${number} selected: ${selectedFile.name}`
                        : `Screenshot ${number} upload`
                    }
                    className="oac-upload oac-upload--screenshot"
                    key={number}
                  >
                    <input
                      accept={PUBLISH_IMAGE_ACCEPT}
                      onChange={handleScreenshotChange(number)}
                      onClick={handleFileFieldActivate}
                      onKeyDown={handleFileFieldActivate}
                      required={!isEditMode || existingScreenshotCount === 0}
                      type="file"
                    />
                    {previewUrl ? (
                      <img
                        alt=""
                        aria-hidden="true"
                        src={previewUrl}
                        style={uploadPreviewImageStyle}
                      />
                    ) : (
                      <span aria-hidden="true" className="oac-upload__symbol">
                        <span className="oac-upload__arrow">↑</span>
                        <span className="oac-upload__tray" />
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {!isEditMode && (
              <label className="oac-publish__agreement">
                <input
                  checked={values.publisherAgreement}
                  name="publisherAgreement"
                  onChange={handleInputChange}
                  required
                  type="checkbox"
                />
                <span>
                  I accept and agree to the{" "}
                  <Link
                    onClick={handleAgreementLinkClick}
                    to={PUBLISHER_AGREEMENT_ROUTE}
                  >
                    OpenAppCart Publisher Agreement.
                  </Link>
                </span>
              </label>
            )}

            {(validationErrors.length > 0 ||
              (!paymentRetryAvailable && formError) ||
              formSuccess ||
              fileFieldErrors.icon ||
              fileFieldErrors.screenshot1 ||
              fileFieldErrors.screenshot2 ||
              fileFieldErrors.screenshot3) && (
              <div className="oac-publish__validation">
                {formSuccess && (
                  <p className="oac-form-message">{formSuccess}</p>
                )}
                {!paymentRetryAvailable && formError && (
                  <p className="oac-form-message oac-form-message--error">{formError}</p>
                )}
                {fileFieldErrors.icon && (
                  <p className="oac-form-message oac-form-message--error">{fileFieldErrors.icon}</p>
                )}
                {[fileFieldErrors.screenshot1, fileFieldErrors.screenshot2, fileFieldErrors.screenshot3]
                  .filter(Boolean)
                  .map((error) => (
                    <p className="oac-form-message oac-form-message--error" key={error}>
                      {error}
                    </p>
                  ))}
                {validationErrors.map((error) => (
                  <p className="oac-form-message oac-form-message--error" key={error}>
                    {error}
                  </p>
                ))}
              </div>
            )}

            {isEditMode && (
              <button
                className={[
                  "oac-publish__button",
                  "oac-publish__button--edit-save",
                  "publish-btn",
                  isFormValid ? "enabled" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                disabled={!isFormValid || submitting}
                type="submit"
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            )}

            {!isEditMode && (
              <div className="oac-publish__submit-slot">
                {paymentRetryAvailable ? (
                  <div className="oac-publish__payment-retry-notice">
                    <p className="oac-publish__payment-retry-message oac-form-message oac-form-message--error">
                      {PAYMENT_CANCELLED_RETRY_MESSAGE}
                    </p>
                    {paymentRetryError ? (
                      <p className="oac-publish__payment-retry-error oac-form-message oac-form-message--error">
                        {paymentRetryError}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <div
                  className="oac-publish__button-wrap"
                  onMouseDown={handleProtectedInteractionCapture}
                  onPointerDown={handleProtectedInteractionCapture}
                >
                  {paymentRetryAvailable ? (
                    <button
                      aria-busy={paymentOpening}
                      className={["oac-publish__button", "publish-btn", "enabled"].join(" ")}
                      disabled={paymentOpening || submitting}
                      onClick={() => void startRazorpayPayment()}
                      type="button"
                    >
                      Retry
                    </button>
                  ) : (
                    <button
                      className={[
                        "oac-publish__button",
                        "publish-btn",
                        isFormValid ? "enabled" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={submitting || paymentOpening || (publishGuestGate.action !== "redirect" && !isFormValid)}
                      type="submit"
                    >
                      {submitting
                        ? "Submitting..."
                        : paymentOpening
                          ? "Opening payment..."
                          : "Publish App"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        </form>
      </div>
    </main>
  );
}
